// Composite a couple's monogram PNG onto a photo before upload.
// Client-side canvas op — cheap, and every shared photo carries their brand.

// iOS Safari's createImageBitmap throws on some HEIC/JPEG variants. Fall back
// to <img>+canvas so we never lose the photo just because the decoder was
// picky.
async function decodeToCanvasSource(blob: Blob): Promise<CanvasImageSource & { width: number; height: number; close?: () => void }> {
  try {
    return await createImageBitmap(blob);
  } catch {
    const url = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('image decode failed'));
      i.src = url;
    });
    // Attach dims so callers can read .width/.height uniformly.
    return Object.assign(img, {
      width: img.naturalWidth,
      height: img.naturalHeight,
      close: () => URL.revokeObjectURL(url),
    });
  }
}

export async function applyFrame(photo: Blob, frameUrl: string | null): Promise<Blob> {
  if (!frameUrl) return photo;
  try {
    const [photoSrc, frameSrc] = await Promise.all([
      decodeToCanvasSource(photo),
      fetch(frameUrl).then((r) => r.blob()).then(decodeToCanvasSource),
    ]);
    const target = photoSrc.width * 0.25;
    const scale = target / frameSrc.width;
    const fw = frameSrc.width * scale;
    const fh = frameSrc.height * scale;
    const margin = photoSrc.width * 0.03;
    const out = await composite(photoSrc, frameSrc, {
      x: photoSrc.width - fw - margin,
      y: photoSrc.height - fh - margin,
      w: fw,
      h: fh,
    });
    photoSrc.close?.();
    frameSrc.close?.();
    return out;
  } catch {
    // Frame compositing is a nice-to-have. If it fails, upload the plain
    // photo rather than dropping it.
    return photo;
  }
}

async function composite(
  base: CanvasImageSource & { width: number; height: number },
  overlay: CanvasImageSource,
  at: { x: number; y: number; w: number; h: number }
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(base.width, base.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2d context unavailable');
    ctx.drawImage(base, 0, 0);
    ctx.drawImage(overlay, at.x, at.y, at.w, at.h);
    return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.9 });
  }
  const canvas = document.createElement('canvas');
  canvas.width = base.width;
  canvas.height = base.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.drawImage(base, 0, 0);
  ctx.drawImage(overlay, at.x, at.y, at.w, at.h);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob null'))),
      'image/jpeg',
      0.9
    )
  );
}
